import * as React from 'react'
import { useState } from 'react'
import DashboardContainer from 'components/smart/dashboard-container/DashboardContainer'
import { Box, Grid, Typography, CardMedia } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import AttendanceFilter from '../components/AttendanceFilter/AttendanceFilter'
import defaultImage from 'assets/images/default_avatar.png'
import { renderDay } from '../../../components/custom/AdxLabelRenderer'
import AttendanceLabelRenderer from '../../../components/custom/AdxLabelRenderer'
import AdxTableV2 from 'components/custom/AdxTableV2'
import { format, subDays } from 'date-fns'
import AttendanceInfoDrawer from '../components/AttendanceInfoDrawer'
import { ChevronRight, TimerOutlined } from '@mui/icons-material'
import {
  getAllAttendanceIndividual,
  getAllAttendanceIndividualBiometric,
  exportIndividualAttendanceToCsv,
} from 'services/api/time-attendance'
import { useQuery } from 'react-query'
import AdxButton from 'components/custom/AdxButton'
import { formatTimeToAmPm, formatTimeToAmPmWithSeconds } from 'services/helper/DateTime'
import AdxCircularLoad from 'components/custom/AdxCircularLoad'
import AdxToggle from 'components/custom/AdxToggle'
import ShiftDetails from '../components/ShiftDetails'
import AdxExportButton from 'components/custom/AdxExportButton'
import { handleCsvExport } from 'services/helper/csvExport'
import SnackbarRecruitment from 'module/recruitment/reusable/SnackbarRecruitment'

const fallbackFromNumericStatus = (status) => {
  switch (status) {
    case 1:
      return 'ongoing'
    case 2:
      return 'present'
    case 3:
      return 'incomplete'
    case 4:
      return 'absent'
    case 5:
      return 'late'
    case 6:
      return 'late_and_incomplete'
    case 8:
      return 'on_leave'
    default:
      return 'unknown'
  }
}

const isToday = (date) => date && new Date(date).toDateString() === new Date().toDateString()

const getAttendanceStatus = (attendanceDay) => {
  const status =
    attendanceDay?.attendance?.derived_status ||
    attendanceDay?.derived_status ||
    fallbackFromNumericStatus(attendanceDay?.attendance?.status)

  if (status !== 'unknown') return status
  if (attendanceDay?.is_working_day === false) return 'non_working_day'
  if (isToday(attendanceDay?.date || attendanceDay?.attendance?.date)) return 'not_clocked_in_yet'
  return status
}

const getRequestErrorMessage = (error) => {
  return (
    error?.data?.message ||
    error?.response?.data?.message ||
    error?.message ||
    'Unable to load attendance records.'
  )
}

const AttendanceTableError = ({ error, onRetry }) => (
  <Box
    sx={{
      minHeight: 160,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 1.5,
      borderTop: '1px solid',
      borderColor: 'gray-line.main',
    }}
  >
    <Typography color="error.main" fontWeight={600}>
      Unable to load attendance records
    </Typography>
    <Typography variant="body2" color="text.secondary" textAlign="center">
      {getRequestErrorMessage(error)}
    </Typography>
    <AdxButton variant="outlined" size="small" onClick={onRetry}>
      Retry
    </AdxButton>
  </Box>
)

const AttendanceTrackingIndividual = () => {
  const staffId = useParams()
  // user banner related
  const [staffName, setStaffName] = useState('')
  const [staffPic, setStaffPic] = useState('')
  const [staffTitle, setStaffTitle] = useState('')
  const [staffDept, setStaffDept] = useState('')
  const [totHours, setTotHours] = useState('')
  const [avgHours, setAvgHours] = useState('')

  // date related
  const today = new Date()
  const oneWeekBefore = subDays(today, 6)

  // search related

  // filter related
  const [filterDrawerState, setFilterDrawerState] = useState({ right: false })
  const initialFilterValues = {
    startDate: oneWeekBefore,
    endDate: today,
    duration: '',
    dept: 'all',
    status: 'all',
  }
  const [filterValues, setFilterValues] = useState(initialFilterValues)
  const [exportLoading, setExportLoading] = useState(false)
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false)
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const formattedStartDate = format(filterValues.startDate, 'yyyy-MM-dd')
  const formattedEndDate = format(filterValues.endDate, 'yyyy-MM-dd')
  const handleThisWeek = () => {
    setFilterValues(initialFilterValues)
  }


  // pagination related
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  // tab related
  const [view, setView] = useState('time')
  const handleTabChange = (event, newView) => {
    if (newView !== null) {
      setView(newView)
    }
    setFilterValues(initialFilterValues)
    setRowsPerPage(10)
    setPage(0)
  }
  const tabs = [
    {
      label: 'Time Attendance',
      value: 'time',
    },
    {
      label: 'Biometric',
      value: 'biometric',
    },
  ]

  // API CALL
  const {
    isLoading: isAttendanceDataLoading,
    isFetching: isAttendanceDataFetching,
    isError: isAttendanceDataError,
    error: attendanceDataError,
    data: attendanceData,
    refetch: refetchAttendanceData,
  } = useQuery(
    ['attendanceDataIndividual', filterValues, page, rowsPerPage, view], // Dependencies
    async () => {
      const { data } = await getAllAttendanceIndividual(
        staffId?.id,
        formattedStartDate,
        formattedEndDate,
        page,
        rowsPerPage
      )
      setStaffName(data?.staff?.name)
      setStaffPic(data?.staff?.profile_image)
      setStaffTitle(data?.staff?.position)
      setStaffDept(data?.staff?.department)
      setTotHours(data?.total_worked_hours)
      setAvgHours(data?.average_worked_hours)
      return data
    },
    {
      enabled: view === 'time', // Fetch only when view is 'time'
    }
  )

  const {
    isLoading: isBiometricLoading,
    isFetching: isBiometricFetching,
    isError: isBiometricError,
    error: biometricError,
    data: biometricAttendanceData,
    refetch: refetchBiometricData,
  } = useQuery(
    ['attendanceDataIndividualBiometric', view, filterValues, page, rowsPerPage], // Dependencies
    async () => {
      const { data } = await getAllAttendanceIndividualBiometric(
        staffId?.id,
        formattedStartDate,
        formattedEndDate
      )
      return data
    },
    {
      enabled: view === 'biometric', // Fetch only when view is 'biometric'
    }
  )

  const attendanceRows = attendanceData?.attendances || []
  const biometricRows = biometricAttendanceData?.data || []
  const count = attendanceData?.meta?.total || 0
  const attendanceLoading = isAttendanceDataLoading || (isAttendanceDataFetching && !attendanceData)
  const biometricLoading = isBiometricLoading || (isBiometricFetching && !biometricAttendanceData)
  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // Handle CSV export for individual staff
  const handleExport = async (exportData) => {
    try {
      setExportLoading(true)

      const response = await exportIndividualAttendanceToCsv(staffId?.id, exportData)
      const result = handleCsvExport(response, `attendance_${staffName || 'staff'}.csv`)

      if (result.success) {
        setSnackbarMessage(`Attendance records exported successfully as ${result.filename}`)
        setSuccessSnackbarOpen(true)
      } else {
        setSnackbarMessage('Failed to export attendance records')
        setErrorSnackbarOpen(true)
      }
    } catch (error) {
      console.error('Export error:', error)
      setSnackbarMessage('Failed to export attendance records')
      setErrorSnackbarOpen(true)
    } finally {
      setExportLoading(false)
    }
  }

  // attendance info drawer related
  const [drawerState, setDrawerState] = useState({ right: false })
  const [attendanceDetails, setAttendanceDetails] = useState({
    profile_image: '',
    date: '',
    staffId: '',
    staffName: '',
    staffTitle: '',
    staffDept: '',
    status: '',
    type: '',
    clockInTime: '',
    clockInLocation: '',
    clockInNote: '',
    clockOutTime: '',
    clockOutLocation: '',
    clockOutNote: '',
    duration: '',
    overtime: '',
    breakTime: '',
    isHoliday: '',
  })

  const navigate = useNavigate()

  const handleOpenDrawer = (row) => {
    const status = getAttendanceStatus(row)
    setAttendanceDetails({
      profile_image: staffPic,
      date: row.date,
      staffId: staffId,
      staffName: staffName,
      staffTitle: staffTitle,
      staffDept: staffDept,
      status,
      type: row.attendance?.clockin?.mode_id,
      clockInTime: row.attendance?.clockin?.clockin,
      clockInLocation: row.attendance?.clockin?.street,
      clockInNote: row.attendance?.clockin?.remarks,
      clockOutTime: row.attendance?.clockout?.clockout,
      clockOutLocation: row.attendance?.clockout?.street,
      clockOutNote: row.attendance?.clockout?.remarks,
      duration: row.attendance?.total_hours,
      overtime: row.attendance?.overtime,
      breakTime: row.attendance?.break_duration,
      isHoliday: row.is_holiday,
    })
    setDrawerState({ right: true })
  }


  return (
    <DashboardContainer
      headerTitle={
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Typography
            onClick={() => navigate(-1)}
            sx={{ cursor: 'pointer', color: 'alternate.main' }}
          >
            Attendance Tracking
          </Typography>
          <ChevronRight sx={{ color: 'light-gray-text.main' }} />
          <Typography>{staffName}</Typography>
        </Box>
      }
      blockMobile
      isBeta
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box
          sx={{
            minHeight: 89,
            bgcolor: 'white.main',
            border: '1px solid',
            borderColor: 'gray-line.main',
            borderRadius: '4.5px',
            display: 'flex',
            justifyContent: 'space-between',
            p: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'start',
              gap: 2,
              width: '45%',
            }}
          >
            <CardMedia
              component="img"
              sx={{
                height: 45,
                width: 45,
                objectFit: 'cover',
                borderRadius: '50%',
              }}
              src={staffPic || defaultImage}
            />
            <Box
              sx={{
                wordWrap: 'break-word',
                wordBreak: 'break-all',
                whiteSpace: 'normal',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-around',
                alignItems: 'start',
                maxHeight: 80,
              }}
            >
              <Typography
                sx={{
                  maxHeight: 40,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'break-all',
                }}
              >
                {staffName}
              </Typography>
              <Typography variant="body2" sx={{ color: 'light-gray-text.main' }}>
                {staffTitle}, {staffDept}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              borderLeft: '1px solid',
              borderColor: 'gray-line.main',
              px: 2,
              alignItems: 'center',
              width: '27.5%',
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '50%',
                backgroundColor: 'alternate.light',
              }}
            >
              <TimerOutlined sx={{ color: 'alternate.main' }} />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ color: 'dark-text.main' }}>
                {totHours} hours
              </Typography>
              <Typography
                sx={{
                  color: 'light-gray-text.main',
                  fontVariant: 'body2',
                }}
              >
                Total hours work
              </Typography>
            </Box>
          </Box>
          <Box
            item
            sx={{
              display: 'flex',
              gap: 2,
              px: 2,
              alignItems: 'center',
              width: '27.5%',
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '50%',
                backgroundColor: 'alternate.light',
              }}
            >
              <TimerOutlined sx={{ color: 'alternate.main' }} />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ color: 'dark-text.main' }}>
                {avgHours} hours
              </Typography>
              <Typography
                sx={{
                  color: 'light-gray-text.main',
                  fontVariant: 'body2',
                }}
              >
                Daily average hours
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Grid
            container
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid item xs={4}>
              <Typography
                variant="subtitle1"
                sx={{ color: 'light-gray-text.main', display: { xs: 'none', md: 'block' } }}
              >
                Daily attendance record
              </Typography>
            </Grid>
            <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <AdxToggle items={tabs} view={view} handleTabChange={handleTabChange} />
            </Grid>
            <Grid item xs={4} sx={{ textAlign: 'end' }}>
              <ShiftDetails shiftId={attendanceData?.shift?.id} />
            </Grid>
          </Grid>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'gray-line.main',
              backgroundColor: 'white.main',
              borderRadius: '4.5px',
              overflow: 'clip',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { md: 'column', lg: 'row' },
                justifyContent: 'space-between',
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'gray-line.main',
              }}
            >
              <Box sx={{ overflow: 'auto', display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                <AttendanceFilter
                  filterValues={filterValues}
                  setFilterValues={setFilterValues}
                  drawerState={filterDrawerState}
                  setDrawerState={setFilterDrawerState}
                  stickyHeader={true}
                  disableDepartmentSelector
                  disableFilterButton
                />
                <AdxButton onClick={handleThisWeek} variant={'outlined'}>
                  This Week
                </AdxButton>
                <AdxExportButton
                  onExport={handleExport}
                  loading={exportLoading}
                  disabled={attendanceRows.length === 0}
                  variant="outlined"
                  size="small"
                />
              </Box>
            </Box>
            {view === 'time' && (
              <Box sx={{ width: '100%' }}>
                {attendanceLoading ? (
                  <AdxCircularLoad />
                ) : isAttendanceDataError ? (
                  <AttendanceTableError
                    error={attendanceDataError}
                    onRetry={() => refetchAttendanceData()}
                  />
                ) : (
                  <AdxTableV2
                    column={timeTableColumn}
                    data={attendanceRows}
                    allColumnSorted={false}
                    cellBorder={false}
                    fullWidth={true}
                    onClick={handleOpenDrawer}
                    stickyHeader={true}
                    stickyFirstColumn={true}
                    height={{ xs: 'calc(100vh - 460px)', lg: 'calc(100vh - 430px)' }}
                    rowClickable
                    pagination
                    count={count}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    isLoading={attendanceLoading}
                    highlightWhenHovered
                  />
                )}
              </Box>
            )}

            {view === 'biometric' && (
              <Box sx={{ width: '100%' }}>
                {biometricLoading ? (
                  <AdxCircularLoad />
                ) : isBiometricError ? (
                  <AttendanceTableError
                    error={biometricError}
                    onRetry={() => refetchBiometricData()}
                  />
                ) : (
                  <AdxTableV2
                    column={biometricTableColumn}
                    data={biometricRows}
                    allColumnSorted={false}
                    cellBorder={false}
                    fullWidth={true}
                    // onClick={handleOpenDrawer}
                    stickyHeader={true}
                    stickyFirstColumn={true}
                    height={{ xs: 'calc(100vh - 390px)', lg: 'calc(100vh - 430px)' }}
                    // rowClickable
                    // pagination
                    count={count}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    isLoading={biometricLoading}
                    highlightWhenHovered
                  />
                )}
              </Box>
            )}

            <AttendanceInfoDrawer
              drawerState={drawerState}
              setDrawerState={setDrawerState}
              attendanceDetails={attendanceDetails}
              disableViewStaff
            />
          </Box>
        </Box>
      </Box>

      {/* Snackbar notifications */}
      {successSnackbarOpen && (
        <SnackbarRecruitment
          autoHideDuration={3000}
          status="success"
          TransitionComponent="slide"
          message={snackbarMessage}
          open={successSnackbarOpen}
          handleClose={() => setSuccessSnackbarOpen(false)}
        />
      )}
      {errorSnackbarOpen && (
        <SnackbarRecruitment
          autoHideDuration={3000}
          status="error"
          TransitionComponent="slide"
          message={snackbarMessage}
          open={errorSnackbarOpen}
          handleClose={() => setErrorSnackbarOpen(false)}
        />
      )}
    </DashboardContainer>
  )
}

export default AttendanceTrackingIndividual

const timeTableColumn = [
  {
    Header: 'Date',
    accessor: 'date',
    Cell: (props) => {
      const { value } = props
      return (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            p: 2,
          }}
        >
          <Typography>{renderDay(value)}</Typography>
        </Box>
      )
    },
  },
  {
    Header: 'Clock In',
    accessor: 'clock_in',
    Cell: (props) => {
      return (
        <Box sx={{ p: 2 }}>
          <Typography sx={{}}>
            {props?.row?.original?.attendance?.clockin?.clockin
              ? formatTimeToAmPm(props?.row?.original?.attendance?.clockin?.clockin)
              : '--:--'}
          </Typography>
        </Box>
      )
    },
  },
  {
    Header: 'Clock Out',
    accessor: 'clock_out',
    Cell: (props) => {
      return (
        <Box sx={{ p: 2 }}>
          <Typography sx={{}}>
            {props?.row?.original?.attendance?.clockout?.clockout
              ? formatTimeToAmPm(props?.row?.original?.attendance?.clockout?.clockout)
              : '--:--'}
          </Typography>
        </Box>
      )
    },
  },
  {
    Header: 'Duration',
    accessor: 'duration',
    Cell: (props) => {
      return (
        <Box sx={{ p: 2 }}>
          <Typography sx={{}}>
            {props?.row?.original?.attendance?.total_hours
              ? `${props?.row?.original?.attendance?.total_hours} hrs`
              : '--:--'}
          </Typography>
        </Box>
      )
    },
  },
  {
    Header: 'Break Time',
    accessor: 'break_time',
    Cell: (props) => {
      return (
        <Box sx={{ p: 2 }}>
          <Typography sx={{}}>
            {props?.row?.original?.attendance?.break_duration
              ? `${props?.row?.original?.attendance?.break_duration} hrs`
              : '--:--'}
          </Typography>
        </Box>
      )
    },
  },
  {
    Header: 'Overtime',
    accessor: 'overtime',
    Cell: (props) => {
      return (
        <Box sx={{ p: 2 }}>
          <Typography sx={{}}>
            {props?.row?.original?.attendance?.overtime
              ? `+ ${props?.row?.original?.attendance?.overtime}`
              : '--:--'}
          </Typography>
        </Box>
      )
    },
  },
  {
    Header: 'Status',
    accessor: 'status',
    Cell: (props) => {
      const status = getAttendanceStatus(props?.row?.original)
      return (
        <Box sx={{ p: 2, width: 'fit-content', maxWidth: '200px' }}>
          <AttendanceLabelRenderer
            status={status}
            withText
            withChip
            forDaily
            isHoliday={props?.row?.original?.is_holiday}
          />
        </Box>
      )
    },
  },
]

const biometricTableColumn = [
  {
    Header: 'Date',
    accessor: 'verification_timestamp',
    id: 'date_column',
    Cell: (props) => {
      const { value } = props
      return (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            p: 2,
          }}
        >
          <Typography>{renderDay(value)}</Typography>
        </Box>
      )
    },
  },
  {
    Header: 'Time Stamp',
    accessor: 'verification_timestamp',
    id: 'timestamp_column',
    Cell: (props) => {
      const { value } = props
      return (
        <Box sx={{ p: 2 }}>
          <Typography>{formatTimeToAmPmWithSeconds(value)}</Typography>
        </Box>
      )
    },
  },
  {
    Header: 'IP Address',
    accessor: 'ip_address',
    Cell: (props) => {
      const { value } = props
      return (
        <Box sx={{ p: 2 }}>
          <Typography sx={{}}>{value}</Typography>
        </Box>
      )
    },
  },
  {
    Header: 'Location',
    accessor: 'device_id',
    Cell: (props) => {
      return (
        <Box sx={{ p: 2 }}>
          <Typography sx={{}}>{props?.row?.original?.device_id}</Typography>
        </Box>
      )
    },
  },
]
